import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
// Canonicalise role literal
export const normRole = (r) => (r ? String(r).replace(/[^a-z0-9]/gi, "").toLowerCase() : "");


const AuthContext = createContext(null);

// "store_keeper" all resolve to the same key. Belt-and-braces against DB drift.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
      localStorage.removeItem("access_token");
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    if (data?.token) localStorage.setItem("access_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout is best-effort — the server may be down or the session already
      // expired. We still purge the local token so the UI reflects logged-out.
      // eslint-disable-next-line no-console
      console.warn("Logout request failed (ignored):", err?.message || err);
    }
    localStorage.removeItem("access_token");
    setUser(false);
  };

  const value = useMemo(() => {
    const perms = new Set(user?.effective_permissions || []);
    const roleKey = normRole(user?.role);
    const isSupervisor = roleKey === "supervisor";
    // Defensive fallback: if the backend response didn't ship
    // `effective_permissions` (e.g. an older deployed build that doesn't yet
    // know about the storekeeper role), still respect the role's canonical
    // default permission set on the client. Keep in sync with
    // backend/models.py::ROLE_DEFAULTS.
    const ROLE_FALLBACK = {
      supervisor: [
        "view_records", "create_iv", "create_request", "approve_request",
        "mark_return", "manage_users", "manage_master", "add_stock",
        "delete_records", "export_csv", "view_audit",
      ],
      admin: [
        "view_records", "create_iv", "create_request",
        "mark_return", "add_stock", "export_csv", "view_audit",
      ],
      storekeeper: [
        "view_records", "create_iv", "create_request", "approve_request",
        "mark_return", "manage_master", "add_stock", "export_csv",
      ],
      engineer: ["create_request"],
    };
    const roleFallback = new Set(ROLE_FALLBACK[roleKey] || []);
    // Only apply the role fallback when the backend didn't ship the field at
    // all (older deployed build). An explicit empty array means a supervisor
    // deliberately revoked all perms and we should respect that.
    const backendPermsMissing = !!user && !("effective_permissions" in user);
    const can = (perm) => {
      if (isSupervisor) return true;
      if (perms.has(perm)) return true;
      if (backendPermsMissing && roleFallback.has(perm)) return true;
      return false;
    };
    // Expose a normalised role check helper so pages don't rely on literal
    // string equality (`user.role === "storekeeper"`) which drifts silently
    // if the DB ever stores a different casing/spacing.
    const hasRole = (...roles) => roles.map(normRole).includes(roleKey);
    return { user, ready, login, logout, refresh, can, hasRole };
  }, [user, ready, login, logout, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
